/**
 * Imperative toast system backed by @agion/ui Radix Toast components.
 *
 * Usage:
 *   import { toast } from '@/lib/toast'
 *   toast.success('Saved')
 *   toast.error('Something went wrong')
 *   toast({ title: 'Custom', variant: 'warning' })
 */

export type ToastVariant = 'default' | 'success' | 'error' | 'warning' | 'info'

export interface ToastEntry {
  id: string
  title: string
  description?: string
  variant: ToastVariant
  duration: number
}

interface ToastOptions {
  title?: string
  description?: string
  variant?: ToastVariant | 'destructive'
  duration?: number
  id?: string
}

// ---------------------------------------------------------------------------
// Module-level store (works outside React)
// ---------------------------------------------------------------------------

let toasts: ToastEntry[] = []
let listeners: Array<() => void> = []
let nextId = 0

function emit() {
  // Freeze a new snapshot so useSyncExternalStore detects the change
  toasts = [...toasts]
  for (const fn of listeners) fn()
}

function addToast(entry: ToastEntry) {
  // Deduplicate by id — if a toast with this id already exists, replace it
  const idx = toasts.findIndex((t) => t.id === entry.id)
  if (idx !== -1) {
    toasts[idx] = entry
  } else {
    toasts.push(entry)
  }
  emit()

  // Auto-dismiss
  if (entry.duration > 0) {
    setTimeout(() => dismiss(entry.id), entry.duration)
  }
}

export function dismiss(id: string) {
  toasts = toasts.filter((t) => t.id !== id)
  emit()
}

// ---------------------------------------------------------------------------
// useSyncExternalStore integration
// ---------------------------------------------------------------------------

export function subscribe(callback: () => void) {
  listeners.push(callback)
  return () => {
    listeners = listeners.filter((fn) => fn !== callback)
  }
}

export function getSnapshot(): ToastEntry[] {
  return toasts
}

// ---------------------------------------------------------------------------
// Normalise variant — maps 'destructive' → 'error'
// ---------------------------------------------------------------------------

function normaliseVariant(v?: ToastVariant | 'destructive'): ToastVariant {
  if (v === 'destructive') return 'error'
  return v ?? 'default'
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

const DEFAULT_DURATION = 5000

function createId(): string {
  return `toast-${++nextId}-${Date.now()}`
}

/**
 * Show a toast notification.
 *
 * Supports two call signatures:
 *   toast('Simple message')
 *   toast({ title: 'Title', description: '...', variant: 'success' })
 */
function toast(messageOrOpts: string | ToastOptions, opts?: ToastOptions): string {
  let entry: ToastEntry

  if (typeof messageOrOpts === 'string') {
    const o = opts ?? {}
    const id = o.id ?? createId()
    entry = {
      id,
      title: messageOrOpts,
      description: o.description,
      variant: normaliseVariant(o.variant),
      duration: o.duration ?? DEFAULT_DURATION,
    }
  } else {
    const o = messageOrOpts
    const id = o.id ?? createId()
    entry = {
      id,
      title: o.title ?? '',
      description: o.description,
      variant: normaliseVariant(o.variant),
      duration: o.duration ?? DEFAULT_DURATION,
    }
  }

  addToast(entry)
  return entry.id
}

toast.success = (message: string, opts?: Omit<ToastOptions, 'variant'>) =>
  toast(message, { ...opts, variant: 'success' })

toast.error = (message: string, opts?: Omit<ToastOptions, 'variant'>) =>
  toast(message, { ...opts, variant: 'error' })

toast.warning = (message: string, opts?: Omit<ToastOptions, 'variant'>) =>
  toast(message, { ...opts, variant: 'warning' })

toast.info = (message: string, opts?: Omit<ToastOptions, 'variant'>) =>
  toast(message, { ...opts, variant: 'info' })

toast.dismiss = dismiss

export { toast }
